import { useTableColumns } from "@/appComponents/Routes/planning/useTableColumns";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { boxObjClassFieldIds, enumObjClassNames, posObjClassFieldIds } from "@coreapp/constants";
import {
  defaultPlanningTabularFields,
  LOCAL_STORAGE_KEY_PLANNING_TABULAR_COLUMNS,
  planningTabularFieldDeps,
  TABLE_INITIAL_STATE,
  TABLE_PAGINATION_DEFUALT_STATE
} from "@coreapp/CoreApp/position-management.constants";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode
} from "react";
import {
  ColumnOrderState,
  ExpandedState,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  PaginationState,
  ColumnFiltersState
} from "@tanstack/react-table";
import { cloneReactChildren, DataTable } from "@theorgchartdev/storybook";
import {
  getFieldInfoFromTempColField,
  getPageOffset,
  getRowCanExpand,
  getRowId
} from "@/Utils/Table/PosMgmtUtils";
import { QObjDBFieldDef } from "@coreapp/CoreApp/QObjDBFieldDef";
import { GetSearchResults } from "@/Utils/Promises";
import { useTopic } from "@/hooks/useTopic";
import { useTableHighlightNavigation } from "@/appComponents/Routes/planning/useTableHighlightNavigation";
import { QPlanManager } from "@coreapp/CoreApp/QPlanManager";
import { GetTabularFieldDefs } from "@/Utils/DataLib";
import { toServerFilterValue, deduplicateFilterValues } from "@coreapp/utils/FormatFieldValue";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";

export interface PlanningTabularState {
  columns: QObjDBFieldDef[];
  results: any[];
  isFetching: boolean;
  isSearchLoading: boolean;
  sorting: SortingState;
  columnOrder: ColumnOrderState;
  rowSelection: RowSelectionState;
  expanded: ExpandedState;
  pagination: PaginationState;
  columnFilters: ColumnFiltersState;
  paginationInfo: PaginationInfo;
}

interface PaginationInfo {
  hasMore: boolean;
  limit: number;
  nextOffset: number;
  offset: number;
  totalCount: number;
}

interface QueryOptions {
  mode: "planning_tabular";
  includeDeletes: true;
  handlePositions: boolean;
  pageSize: number;
  offset: number;
  sort: {
    fieldName: string;
    order: "asc" | "desc";
    forHolder: boolean;
  } | null;
  filterState: { field: string; value: any[]; forHolder: boolean }[] | null;
  findPageByEntity?: string;
}

interface LocalStorageData {
  selectedColumns: { fieldID: string; objClassName: string }[];
  columnOrder: string[];
}

interface PlanningTabularContextType {
  state: PlanningTabularState;
  searchCtrlRef: React.RefObject<any>;
  isFetching: boolean;
  isSearchLoading: boolean;
  selectedRowsCount: number;
  projectFields: QObjDBFieldDef[];
  holderFields: QObjDBFieldDef[];
  queryOptions: QueryOptions;
  tableRef: React.RefObject<HTMLDivElement>;
  toastHighlightSelectionRef: React.MutableRefObject<RowSelectionState | null>;
  toastHighlightExpansionRef: React.MutableRefObject<ExpandedState | null>;
  updateState: (updatedState: Partial<PlanningTabularState>) => void;
  setColumns: (columns: QObjDBFieldDef[]) => void;
  resetColumns: () => void;
  setResults: (results: any[]) => void;
  shouldFetch: (isFetching: boolean) => void;
  callQuerySvrApi: () => void;
  onSortingChange: OnChangeFn<SortingState>;
  onColumnOrderChange: OnChangeFn<ColumnOrderState>;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  onExpandedChange: OnChangeFn<ExpandedState>;
  onPaginationChange: OnChangeFn<PaginationState>;
  getLocalStorageCache: () => LocalStorageData | undefined;
  setLocalStorageCache: (update: Partial<LocalStorageData>) => void;
  fetchColumnFilterOptions: (args: {
    columnId: string;
    pageIndex?: number | null;
    pageSize?: number | null;
    searchText?: string | null;
  }) => Promise<{
    data: any[];
    hasMore: boolean;
    pageIndex: number;
    pageSize: number;
  }>;
  getDataCache: () => Record<string, any>;
  updateDataCache: (updatedData: Record<string, any>) => void;
}

interface TableInstanceContextType {
  selectionMap: Record<string, any>;
  selectedPositionsCount: number;
  isOnlyClosedPositionsSelected: () => boolean;
  isClosedOrUnassignedPositionsSelected: () => boolean;
  isOnlyActivePositionsSelected: () => boolean;
}

interface PlanningTabularProviderProps {
  children: ReactNode;
}

interface DataTableProviderProps {
  children: ReactNode;
}
interface TableInstanceProviderProps {
  children: ReactNode;
}

const PlanningTabularContext = createContext<PlanningTabularContextType | undefined>(undefined);
const TableInstanceContext = createContext<TableInstanceContextType | undefined>(undefined);

const PlanningTabularProvider = ({ children }: PlanningTabularProviderProps): JSX.Element => {
  const searchCtrlRef = useRef<any>(null);
  const searchQueryRef = useRef<string>(); // To store the previous search query string for comparison
  const tableRef = useRef<HTMLDivElement>(null);
  const dataCache = useRef<Record<string, any>>({}); // Cache loaded positions(and attached holders) data
  const [state, setState] = useState<PlanningTabularState>({
    columns: [],
    results: [],
    isFetching: false, // indicates if the data fetch is in progress
    isSearchLoading: false, // indicates if the search input is loading data
    sorting: [], // sorting state of the table maintained outside of the DataTable
    columnOrder: [], // column order state maintained outside of the DataTable
    rowSelection: {}, // row selection state maintained outside of the DataTable
    expanded: {}, // expanded state maintained outside of the DataTable
    columnFilters: [], // column filters state maintained outside of the DataTable
    pagination: TABLE_PAGINATION_DEFUALT_STATE, // column order state maintained outside of the DataTable
    paginationInfo: {} as PaginationInfo
  });
  const latestStateRef = useRef(state);

  const wsMgr = GetWSMgr();
  const org = wsMgr?.GetAccountName();
  const doc = GetDoc();
  const chartID = doc?.GetChartID();

  const getDataCache = useCallback(() => {
    return dataCache.current;
  }, []);

  const updateDataCache = useCallback((updatedData: Record<string, any>) => {
    dataCache.current = {
      ...dataCache.current,
      ...updatedData
    };
  }, []);

  /**
   * Generates a unique key for localStorage cache based on organization and chart ID.
   */
  const getLocalStorageCacheKey = useCallback(() => {
    if (!org || !chartID) {
      console.warn("Organization or Chart ID is not defined.");
      return undefined;
    }
    return `${org}_${chartID}`;
  }, [org, chartID]);

  /**
   * Retrieves the localStorage cache for planning table columns.
   */
  const getLocalStorageCache: PlanningTabularContextType["getLocalStorageCache"] =
    useCallback(() => {
      const cacheKey = getLocalStorageCacheKey();
      if (!cacheKey) {
        return;
      }
      const planningTableColumns = localStorage.getItem(LOCAL_STORAGE_KEY_PLANNING_TABULAR_COLUMNS);
      if (planningTableColumns) {
        const cacheData = JSON.parse(planningTableColumns);
        return cacheData[cacheKey];
      }
      return;
    }, [getLocalStorageCacheKey]);

  /**
   * Updates the localStorage cache for planning table columns.
   */
  const setLocalStorageCache: PlanningTabularContextType["setLocalStorageCache"] = useCallback(
    update => {
      const cacheKey = getLocalStorageCacheKey();
      if (!cacheKey) {
        return;
      }

      let cacheData: Record<string, LocalStorageData> = {};
      const planningTableColumns = localStorage.getItem(LOCAL_STORAGE_KEY_PLANNING_TABULAR_COLUMNS);
      if (planningTableColumns) cacheData = JSON.parse(planningTableColumns);
      cacheData[cacheKey] = { ...(cacheData[cacheKey] ?? {}), ...update };
      localStorage.setItem(LOCAL_STORAGE_KEY_PLANNING_TABULAR_COLUMNS, JSON.stringify(cacheData));
    },
    [getLocalStorageCacheKey]
  );

  /**
   * Updates the sorting state of the table.
   * Called when the user changes the sorting order in the table.
   * It updates the sorting state and sets the fetching state to true to trigger a data fetch.
   * Resets selection state and page index to default.
   * @param {function} updater - A function that takes the current sorting state and updates the sorting value in state.
   */
  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    updater => {
      const sorting = typeof updater === "function" ? updater(state.sorting) : updater;

      setState(prev => ({
        ...prev,
        sorting,
        isFetching: true,
        // Reset states to default
        rowSelection: {},
        pagination: {
          ...prev.pagination,
          pageIndex: TABLE_PAGINATION_DEFUALT_STATE.pageIndex
        }
      }));
    },
    [state.sorting]
  );

  /**
   * Updates the column order state of the table and also saves it to the localStorage cache.
   * @param {function} updater - A function that takes the current column order state and updates it.
   */
  const onColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback(updater => {
    setState(prev => {
      const columnOrder = typeof updater === "function" ? updater(prev.columnOrder) : updater;

      setLocalStorageCache({ columnOrder });

      return {
        ...prev,
        columnOrder
      };
    });
  }, []);

  /**
   * Updates the column filter state of the table.
   * @param {function} updater - A function that takes the current column filter state and updates it.
   */
  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = useCallback(
    updater => {
      const columnFilters = typeof updater === "function" ? updater(state.columnFilters) : updater;

      setState(prev => ({
        ...prev,
        columnFilters,
        isFetching: true,
        // Reset states to default
        rowSelection: {},
        pagination: {
          ...prev.pagination,
          pageIndex: TABLE_PAGINATION_DEFUALT_STATE.pageIndex
        }
      }));
    },
    [state.columnFilters]
  );

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    updater => {
      const pagination = typeof updater === "function" ? updater(state.pagination) : updater;

      setState(prev => ({
        ...prev,
        pagination,
        isFetching: true
      }));
    },
    [state.pagination]
  );

  /**
   *  Sets the fetching state to true or false.
   *  Used to indicate whether data should be fetched.
   * @param {boolean} isFetching
   */
  const shouldFetch = useCallback((isFetching: boolean) => {
    setState(prev => ({
      ...prev,
      isFetching
    }));
  }, []);

  /**
   * Updates any part of the state with the provided updatedState object.
   */
  const updateState = useCallback((updatedState: Partial<PlanningTabularState>) => {
    setState(prev => ({ ...prev, ...updatedState }));
  }, []);

  useEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  const {
    toastHighlightSelectionRef,
    toastHighlightExpansionRef,
    pendingToastScrollRef,
    pendingFindPageByEntityRef
  } = useTableHighlightNavigation({
    tableRef,
    latestStateRef,
    searchCtrlRef,
    getDataCache,
    updateState,
    state
  });

  /**
   * Updates the row selection state of the table.
   * @param {function} updater - A function that takes the current row selection state and updates it.
   */
  const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(updater => {
    toastHighlightSelectionRef.current = null;
    toastHighlightExpansionRef.current = null;
    pendingToastScrollRef.current = null; // cancel deferred toast scroll on manual selection
    setState(prev => {
      const selectionState = typeof updater === "function" ? updater(prev.rowSelection) : updater;

      return {
        ...prev,
        rowSelection: selectionState
      };
    });
  }, []);

  const onExpandedChange: OnChangeFn<ExpandedState> = useCallback(updater => {
    toastHighlightExpansionRef.current = null;
    setState(prev => ({
      ...prev,
      expanded: typeof updater === "function" ? updater(prev.expanded) : updater
    }));
  }, []);

  /**
   * Sets the columns state with the provided column definitions.
   * Sets the fetching state to true to indicate that data should be fetched.
   * @param {QObjDBFieldDef[]} columns - The column definitions to set.
   */
  const setColumns = useCallback((columns: QObjDBFieldDef[]) => {
    setState(prev => ({
      ...prev,
      columns,
      isFetching: true
    }));
  }, []);

  /**
   * Resets the column order state to the cached column order from localStorage.
   */
  const resetColumnOrder = useCallback(() => {
    const cachedConfig = getLocalStorageCache();
    setState(prev => ({ ...prev, columnOrder: cachedConfig?.columnOrder ?? [] }));
  }, [getLocalStorageCache]);

  /**
   *  Resets the columns to the default planning tabular field defs.
   *  Sets the fetching state to true to indicate that data should be fetched.
   */
  const resetColumns = useCallback(() => {
    const doc = GetDoc();
    const wsMgr = GetWSMgr();
    if (!doc || !wsMgr) {
      return;
    }

    const objDb = doc.GetObjDB();
    if (!objDb) {
      return;
    }

    const columnDefs: QObjDBFieldDef[] = [];
    const allowedDefs = GetTabularFieldDefs();
    const allowedPosColumnNamesSet = new Set(
      allowedDefs.Position.map((field: QObjDBFieldDef) => field.GetFieldName())
    );
    const allowedBoxColumnNamesSet = new Set(
      allowedDefs.Box.map((field: QObjDBFieldDef) => field.GetFieldName())
    );

    const addFieldDefs = (fieldDefs: { fieldID: string; objClassName: string }[]) => {
      for (const field of fieldDefs) {
        const { fieldID, objClassName } = field;
        const objClass = objDb.GetClassByName(objClassName);
        const fieldDef = objClass?.GetFieldDefByID(fieldID);

        if (!fieldDef) {
          continue;
        }

        const fieldName = fieldDef.GetFieldName();

        if (
          (objClassName === enumObjClassNames.position &&
            allowedPosColumnNamesSet.has(fieldName)) ||
          (objClassName === enumObjClassNames.box && allowedBoxColumnNamesSet.has(fieldName))
        ) {
          columnDefs.push(fieldDef);
        }
      }
    };

    // Read localStorage for cached column config
    const cachedConfig = getLocalStorageCache();
    addFieldDefs(cachedConfig?.selectedColumns ?? []);

    if (columnDefs.length === 0) {
      addFieldDefs(defaultPlanningTabularFields);
    }

    setState(prev => ({
      ...prev,
      columns: columnDefs,
      isFetching: true
    }));
  }, []);

  /**
   * Checks if the search query string has changed compared to the previous one.
   * @returns {[string, boolean]} - Returns the new query string and a boolean indicating if it has changed.
   */
  const searchQueryChanged = (): [string, boolean] => {
    const newQueryString = searchCtrlRef.current?.getQueryString();
    const oldQueryString = searchQueryRef.current;
    const changed = newQueryString !== oldQueryString;
    return [newQueryString, changed];
  };

  const processIgnoredFilters = useCallback(
    (ignoredFilters: { field: string; value: any[]; forHolder: boolean }[]) => {
      return ignoredFilters
        ? state.columnFilters.map(f => {
            const { fieldName, fieldClassName } = getFieldInfoFromTempColField(f.id);
            const ignoredValues = ignoredFilters.filter(
              item =>
                item.field === fieldName &&
                (fieldClassName === enumObjClassNames.box ? item.forHolder : true)
            );
            return {
              ...f,
              value: ignoredValues.length
                ? (f.value as any[]).filter(
                    v => !ignoredValues.some(ignored => ignored.value.includes(v))
                  )
                : f.value
            };
          })
        : state.columnFilters;
    },
    [state.columnFilters]
  );

  /**
   * Sets the results state with the fetched results.
   * Resets the fetching states to false after setting the results.
   * Resets column filters, sorting, selection and pagination states to default if the search query has changed.
   * Updates column filters if there are any ignored filters in the results info.
   * @param {any[]} results - The results fetched from the server or search.
   */
  const setResults = useCallback(
    (sResults: any) => {
      const { results = [], info } = sResults ?? {};

      const paginationInfo = info?.pagination ?? {};

      const columnFilters = processIgnoredFilters(info?.ignoredFilters);

      // Determine if search query string has changed
      const [newQueryString, queryChanged] = searchQueryChanged();
      searchQueryRef.current = newQueryString;

      const resetedState: Partial<typeof state> = {
        sorting: [],
        rowSelection: {},
        expanded: {},
        pagination: TABLE_PAGINATION_DEFUALT_STATE,
        columnFilters: []
      };

      setState(prev => {
        const doc = GetDoc();
        const posObjClass = doc?.GetObjDB()?.GetClassByName(enumObjClassNames.position);
        const posFieldDef = posObjClass?.GetFieldDefByID(posObjClassFieldIds.POSITION_ID);
        const posIndexKey = posFieldDef?.GetFieldName();

        if (!posIndexKey) {
          return prev;
        }

        const globalIndexPage =
          typeof info?.globalIndex === "number" && info.globalIndex >= 0
            ? { pageIndex: Math.floor(info.globalIndex / prev.pagination.pageSize) }
            : null;

        const positionMap: Record<string, any> = {};
        for (let localIndex = 0; localIndex < results.length; localIndex++) {
          const result = results[localIndex];
          const positionId = result[posIndexKey];
          if (positionId) {
            const { __HOLDERS__, ...rest } = result;
            let boxIndexKey: string | undefined;
            if (__HOLDERS__ && __HOLDERS__.length > 0) {
              const boxObjClass = doc?.GetObjDB()?.GetClassByName(enumObjClassNames.box);
              const boxFieldDef = boxObjClass?.GetFieldDefByID(boxObjClassFieldIds.EMPLOYEE_ID);
              boxIndexKey = boxFieldDef?.GetFieldName();
            }
            const subRows = (__HOLDERS__ ?? []).map((holder: { [x: string]: any }) => ({
              ...holder,
              _id: holder[boxIndexKey ?? ""],
              dataType: enumObjClassNames.box
            }));

            const globalIndex = (paginationInfo.offset ?? 0) + localIndex;
            const posDetails = {
              ...rest,
              _id: positionId,
              _globalIndex: globalIndex,
              dataType: enumObjClassNames.position,
              ...(subRows.length > 0 ? { subRows } : {})
            };
            positionMap[positionId] = posDetails;

            // Update the data cache with latest position details
            dataCache.current[positionId] = {
              ...posDetails,
              subRowsMap: subRows.reduce((acc: Record<string, any>, subRow: any) => {
                acc[subRow._id] = subRow;
                return acc;
              }, {})
            };
          }
        }

        const newResults = Object.values(positionMap);

        // Reset states if search query has changed; apply globalIndex page last so it wins
        return {
          ...prev,
          results: newResults,
          isFetching: false,
          paginationInfo,
          columnFilters,
          ...(queryChanged ? resetedState : {}),
          ...(globalIndexPage ? { pagination: { ...prev.pagination, ...globalIndexPage } } : {})
        };
      });
    },
    [processIgnoredFilters]
  );

  /**
   * Generates the project fields(field defs) based on the current columns and their dependencies.
   * @returns {QObjDBFieldDef[]} array of field defs created from columns' state.
   */
  const projectFields = useMemo((): Record<string, QObjDBFieldDef[]> => {
    const doc = GetDoc();
    const objDb = doc?.GetObjDB();

    const fields: Record<string, Record<string, QObjDBFieldDef>> = {};
    const addField = (fieldDef: QObjDBFieldDef) => {
      const fieldID = fieldDef.GetFieldID();
      const fieldObjClassName = fieldDef.GetFieldClassName();
      if (fieldObjClassName) {
        fields[fieldObjClassName] ||= {};
        fields[fieldObjClassName][fieldID] ||= fieldDef;
      }
    };
    for (const fieldDef of state.columns) {
      if (!fieldDef) {
        continue;
      }

      const fieldID = fieldDef.GetFieldID();
      const dependencies =
        planningTabularFieldDeps[fieldID as keyof typeof planningTabularFieldDeps];

      addField(fieldDef);

      if (dependencies && dependencies.length > 0) {
        for (const { fieldID, objClassName } of dependencies) {
          const objClass = objDb?.GetClassByName(objClassName);
          const depFieldDef = objClass?.GetFieldDefByID(fieldID);
          if (!depFieldDef) {
            continue;
          }

          addField(depFieldDef);
        }
      }
    }

    const returnFields: Record<string, QObjDBFieldDef[]> = {
      [enumObjClassNames.position]: [],
      [enumObjClassNames.box]: []
    };
    for (const [objClassName, fieldIdDefMap] of Object.entries(fields)) {
      returnFields[objClassName] = Object.values(fieldIdDefMap);
    }

    // Also send dependant fielddefs like _metrics_* for backend to process and return latest info
    for (const objClassName of [enumObjClassNames.position, enumObjClassNames.box]) {
      returnFields[objClassName] = returnFields[objClassName].flatMap(f => {
        const planDef = f.GetPlanningDef?.();
        return planDef ? [f, planDef] : [f];
      });
    }

    return returnFields;
  }, [state.columns]);

  /**
   * Generates the query options for the search controller based on default states.
   */
  const baseQueryOptions = useMemo((): QueryOptions => {
    const { pageIndex, pageSize } = TABLE_PAGINATION_DEFUALT_STATE;

    const options: QueryOptions = {
      mode: "planning_tabular",
      includeDeletes: true,
      handlePositions: true,
      pageSize,
      offset: getPageOffset(pageIndex, pageSize),
      sort: null,
      filterState: null
    };

    return options;
  }, []);

  /**
   * Generates the query options for needed for Query API calls outside the search controlled, based on the current states.
   */
  const queryOptions = useMemo((): QueryOptions => {
    const sortState = state.sorting;
    const paginationState = state.pagination;

    const options = { ...baseQueryOptions };

    if (sortState && sortState.length > 0) {
      const sortField = sortState[0];
      const { fieldName, fieldClassName } = getFieldInfoFromTempColField(sortField.id);
      if (fieldName) {
        options.sort = {
          fieldName,
          order: sortField.desc ? "desc" : "asc",
          forHolder: fieldClassName === enumObjClassNames.box
        };
      }
    }

    if (paginationState && paginationState.pageSize > 0) {
      options.pageSize = paginationState.pageSize;
      options.offset = getPageOffset(paginationState.pageIndex, paginationState.pageSize);
    }

    if (state.columnFilters.length > 0) {
      options.filterState = state.columnFilters.map(f => {
        const { fieldName, fieldClassName } = getFieldInfoFromTempColField(f.id);
        return {
          field: fieldName,
          value: (f.value as any[]).map(toServerFilterValue),
          forHolder: fieldClassName === enumObjClassNames.box
        };
      });
    }

    return options;
  }, [baseQueryOptions, state.sorting, state.pagination, state.columnFilters]);

  /**
   * Calls the search controller's DoSearch method(that fetches data) with latest query options and project fields.
   */
  const callQuerySvrApi = useCallback(() => {
    if (!searchCtrlRef.current) {
      return;
    }

    const findPageByEntity = pendingFindPageByEntityRef.current;
    pendingFindPageByEntityRef.current = null;
    const effectiveQueryOptions = findPageByEntity
      ? { ...queryOptions, findPageByEntity }
      : queryOptions;
    searchCtrlRef.current.DoSearch({
      queryOrCriteria: searchCtrlRef.current.getQueryString(),
      queryOptions: effectiveQueryOptions,
      projectFields: projectFields[enumObjClassNames.position].map(f => f.GetFieldName()),
      holderProject: projectFields[enumObjClassNames.box].map(f => f.GetFieldName())
    });
  }, [projectFields, queryOptions]);

  const fetchColumnFilterOptions: PlanningTabularContextType["fetchColumnFilterOptions"] =
    useCallback(
      async ({ columnId, pageIndex, pageSize, searchText = "" }) => {
        const currentPageIndex = pageIndex || 0;
        const currentPageSize = pageSize || 50;
        const { fieldName, fieldClassName } = getFieldInfoFromTempColField(columnId);
        const queryOrCriteria = searchCtrlRef.current.getQueryString();
        const { result } = await GetSearchResults(queryOrCriteria, {
          planID: GetWSMgr()?.GetPlanMgr()?.GetPlanID(),
          projectFields: projectFields[enumObjClassNames.position].map(f => f.GetFieldName()),
          holderProject: projectFields[enumObjClassNames.box].map(f => f.GetFieldName()),
          ...baseQueryOptions,
          pageSize: currentPageSize,
          offset: getPageOffset(currentPageIndex, currentPageSize),
          sort: {
            fieldName,
            order: "asc",
            forHolder: fieldClassName === enumObjClassNames.box
          },
          columnFilter: {
            field: fieldName,
            searchText: toServerFilterValue(searchText),
            forHolder: fieldClassName === enumObjClassNames.box
          },
          filterState: state.columnFilters.map(f => {
            const { fieldName, fieldClassName } = getFieldInfoFromTempColField(f.id);
            return {
              field: fieldName,
              value: (f.value as any[]).map(toServerFilterValue),
              forHolder: fieldClassName === enumObjClassNames.box
            };
          })
        });

        const { results, info } = result;

        const data = deduplicateFilterValues(results, fieldName);

        return {
          data,
          hasMore: info?.pagination?.hasMore === true,
          pageIndex: currentPageIndex + 1,
          pageSize: currentPageSize
        };
      },
      [state.columnFilters, projectFields]
    );

  useEffect(() => {
    resetColumns();
    resetColumnOrder();
  }, [resetColumns, resetColumnOrder]);

  // Call the query server API when the fetching state changes.
  useEffect(() => {
    if (!state.isFetching) {
      return;
    }
    callQuerySvrApi();
  }, [callQuerySvrApi, state.isFetching]);

  const contextValue = useMemo(
    (): PlanningTabularContextType => ({
      state,
      searchCtrlRef,
      isFetching: state.isFetching,
      isSearchLoading: state.isSearchLoading,
      selectedRowsCount: Object.values(state.rowSelection).filter(Boolean).length,
      projectFields: projectFields[enumObjClassNames.position],
      holderFields: projectFields[enumObjClassNames.box],
      queryOptions: baseQueryOptions,
      tableRef,
      toastHighlightSelectionRef,
      toastHighlightExpansionRef,
      updateState,
      setColumns,
      resetColumns,
      setResults,
      shouldFetch,
      callQuerySvrApi,
      onSortingChange,
      onColumnOrderChange,
      onColumnFiltersChange,
      onRowSelectionChange,
      onExpandedChange,
      onPaginationChange,
      getLocalStorageCache,
      setLocalStorageCache,
      fetchColumnFilterOptions,
      getDataCache,
      updateDataCache
    }),
    [
      state,
      searchCtrlRef,
      projectFields,
      baseQueryOptions,
      tableRef,
      updateState,
      setColumns,
      resetColumns,
      setResults,
      shouldFetch,
      callQuerySvrApi,
      onSortingChange,
      onColumnOrderChange,
      onColumnFiltersChange,
      onRowSelectionChange,
      onExpandedChange,
      onPaginationChange,
      getLocalStorageCache,
      setLocalStorageCache,
      fetchColumnFilterOptions,
      getDataCache
    ]
  );

  return (
    <PlanningTabularContext.Provider value={contextValue}>
      <DataTableProvider>
        <TableInstanceProvider>{cloneReactChildren(children)}</TableInstanceProvider>
      </DataTableProvider>
    </PlanningTabularContext.Provider>
  );
};

export const TableInstanceProvider = ({ children }: TableInstanceProviderProps): JSX.Element => {
  const {
    state,
    getDataCache,
    updateState,
    toastHighlightSelectionRef,
    toastHighlightExpansionRef
  } = usePlanningTabularContext();

  // selection map, storing selected position details
  // should be re-calculated when selected rows or results change
  const posSelectionMap: Record<string, any> = useMemo(() => {
    const selectionMap: Record<string, any> = {};
    const dataCache = getDataCache();

    for (const [id, isSelected] of Object.entries(state.rowSelection)) {
      if (!isSelected || !(id in dataCache)) {
        continue;
      }
      const data = dataCache[id];
      const dataType = data.dataType;
      if (dataType === enumObjClassNames.position) {
        selectionMap[id] = {
          ...data
        };
      } else {
        console.warn(`Unsupported data type: ${dataType}`);
      }
    }

    return selectionMap;
  }, [state.rowSelection, state.results]);

  // react to overrides changes, reset selection state and refetch
  useTopic("loadedOverrides", (payload: { planMgr: QPlanManager }) => {
    const { planMgr } = payload;
    if (!planMgr) {
      return;
    }

    const pendingSelection = toastHighlightSelectionRef.current;
    const pendingExpansion = toastHighlightExpansionRef.current;
    // Consume refs so they don't leak into subsequent loadedOverrides events
    toastHighlightSelectionRef.current = null;
    toastHighlightExpansionRef.current = null;

    updateState({
      rowSelection: pendingSelection ?? {},
      expanded: pendingExpansion ?? {},
      isFetching: true
    });
  });

  // Derivations and utilities for position selection
  const selectedPositionsCount = Object.values(posSelectionMap).filter(Boolean).length;

  const isOnlyClosedPositionsSelected = useCallback(() => {
    return (
      selectedPositionsCount > 0 &&
      Object.values(posSelectionMap).every(pos => pos && pos.__deleted__)
    );
  }, [selectedPositionsCount, posSelectionMap]);

  const isClosedOrUnassignedPositionsSelected = useCallback(() => {
    return (
      selectedPositionsCount > 0 &&
      Object.values(posSelectionMap).every(pos => pos && (pos.__deleted__ || !pos.subRows?.length))
    );
  }, [selectedPositionsCount, posSelectionMap]);

  const isOnlyActivePositionsSelected = useCallback(() => {
    return (
      selectedPositionsCount > 0 &&
      Object.values(posSelectionMap).every(pos => pos && !pos.__deleted__)
    );
  }, [selectedPositionsCount, posSelectionMap]);

  const contextValue: TableInstanceContextType = useMemo(() => {
    return {
      selectionMap: posSelectionMap,
      selectedPositionsCount,
      isOnlyClosedPositionsSelected,
      isClosedOrUnassignedPositionsSelected,
      isOnlyActivePositionsSelected
    };
  }, [
    posSelectionMap,
    selectedPositionsCount,
    isOnlyClosedPositionsSelected,
    isClosedOrUnassignedPositionsSelected,
    isOnlyActivePositionsSelected
  ]);

  return (
    <TableInstanceContext.Provider value={contextValue}>
      {cloneReactChildren(children)}
    </TableInstanceContext.Provider>
  );
};

const DataTableProvider = ({ children }: DataTableProviderProps): JSX.Element => {
  const {
    state,
    onSortingChange,
    onColumnOrderChange,
    onRowSelectionChange,
    onExpandedChange,
    onPaginationChange,
    onColumnFiltersChange
  } = usePlanningTabularContext();
  const tableColumns = useTableColumns();
  const { planManager } = usePlanManagerContext();
  const isPlanEditable = planManager?.state?.CanUpdatePlan();

  const tableExternalState = useMemo(
    () => ({
      sorting: state.sorting,
      columnOrder: state.columnOrder,
      rowSelection: state.rowSelection,
      expanded: state.expanded,
      pagination: state.pagination,
      columnFilters: state.columnFilters
    }),
    [
      state.sorting,
      state.columnOrder,
      state.rowSelection,
      state.expanded,
      state.pagination,
      state.columnFilters
    ]
  );

  const getTableRowId = useCallback(() => {
    return getRowId();
  }, [state.results]);

  const initialState = useMemo(() => {
    return {
      columnPinning: {
        left: Object.entries(TABLE_INITIAL_STATE.columnPinning.left).flatMap(
          ([objClassName, fieldId]) => `${fieldId}_${objClassName}`
        )
      }
    };
  }, []);

  return (
    <DataTable.Provider
      data={state.results}
      columns={tableColumns}
      initialState={initialState}
      getRowCanExpand={getRowCanExpand}
      state={tableExternalState}
      onSortingChange={onSortingChange}
      onColumnOrderChange={onColumnOrderChange}
      onColumnFiltersChange={onColumnFiltersChange}
      onRowSelectionChange={onRowSelectionChange}
      onExpandedChange={onExpandedChange}
      enableRowSelection={isPlanEditable}
      enableSubRowSelection={isPlanEditable}
      onPaginationChange={onPaginationChange}
      // Required config for server-side ops
      paginateExpandedRows
      manualPagination
      manualFiltering
      rowCount={state.paginationInfo?.totalCount}
      autoResetExpanded={false}
      getRowId={getTableRowId()}
      getSortedRowModel={undefined}
    >
      {children}
    </DataTable.Provider>
  );
};

export const usePlanningTabularContext = (): PlanningTabularContextType => {
  const context = useContext(PlanningTabularContext);

  if (!context) {
    throw new Error("usePlanningTabularContext must be used within PlanningTabularProvider");
  }

  return context;
};

export const useOptionalPlanningTabularContext = (): PlanningTabularContextType | undefined => {
  return useContext(PlanningTabularContext);
};

export const useTableInstanceContext = (): TableInstanceContextType => {
  const context = useContext(TableInstanceContext);
  if (!context) {
    throw new Error("useTableInstanceContext must be used within TableInstanceProvider");
  }
  return context;
};

export default PlanningTabularProvider;
